import { useCallback } from 'react'
import {
  safePtrAccess,
  usePRFile,
  useZoomToFitNode,
} from '@sketch-hq/sketch-web-renderer'

/** How much of the viewport the revealed node is allowed to fill. */
const MAX_COVERAGE = 0.6

interface RevealTarget {
  uuid?: string
  layerPathHash?: string
  /**
   * The entity this sits in, for something the canvas has no node of its own
   * for. A deleted layer is still somewhere - inside the frame it was deleted
   * from - and that frame is on the canvas.
   */
  entityUUID?: string | null
}

/**
 * Travels the canvas to a change picked out of the list.
 *
 * Picking one out of a list is only useful if you can then see it, and a
 * changed layer is as likely to be off screen as on it. The camera goes to the
 * layer itself rather than to the entity around it - a change to one button in
 * a tall frame is not found by fitting the frame.
 *
 * A deleted thing has no node here to travel to, which is what being deleted
 * means, so the camera fits the frame it was deleted from instead - the place
 * on the canvas the change is about. Only if the frame has gone too is there
 * nowhere to go, and then there is nothing to see either.
 *
 * Driven by the click rather than by the selection changing, so selecting the
 * same thing on the canvas does not move the camera out from under the reader
 * who just clicked it.
 *
 * Only fits the node down, never up: a small layer blown up to fill the
 * viewport loses the context that makes it recognisable.
 */
export function useRevealChange() {
  const prFile = usePRFile()
  const zoomToFitNode = useZoomToFitNode()

  return useCallback(
    (target: RevealTarget) => {
      // A pointer into the WASM heap, which the renderer deletes when it
      // re-roots or loads another document - and this runs from a click, so
      // any amount of that can have happened since it was read. Asking a
      // deleted one for a node throws rather than answering null.
      const file = safePtrAccess(prFile)

      if (!file) return

      const own = target.layerPathHash
        ? file.findByLayerPathHash(target.layerPathHash)
        : target.uuid
          ? file.findByUUID(target.uuid)
          : null

      // Its own node if the canvas has one, otherwise whatever holds it.
      const node =
        own ?? (target.entityUUID ? file.findByUUID(target.entityUUID) : null)

      const identifier = node?.getIdentifier()
      if (identifier === undefined) return

      zoomToFitNode(identifier, MAX_COVERAGE, true)
    },
    [prFile, zoomToFitNode]
  )
}
